import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Calendar, MapPin, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";
import { useReportTileEmpty } from "./DashboardTile";

interface Event {
  id: number;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
}

export default function UpcomingEvents() {
  const { data: events, isLoading } = useQuery<Event[]>({
    queryKey: ["/api/events/upcoming"],
  });

  useReportTileEmpty("upcoming_events", !isLoading && (events?.length ?? 0) === 0);

  return (
    <Card className="h-full border border-border shadow-sm">
      <CardHeader className="pb-4 border-b border-border flex flex-row items-center justify-between">
        <SectionHelp contentId="dashboard:events" tooltip="Learn about events">
          <CardTitle className="text-lg font-bold text-foreground">Upcoming Events</CardTitle>
        </SectionHelp>
        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground gap-1 font-semibold">
          All <ArrowRight className="h-3 w-3" />
        </Button>
      </CardHeader>
      
      <CardContent className="p-4 space-y-3">
        {isLoading ? (
          <>
            <EventSkeleton />
            <EventSkeleton />
            <EventSkeleton />
          </>
        ) : events && events.length > 0 ? (
          events.slice(0, 3).map((event) => (
            <div 
              key={event.id} 
              className={cn(
                "group flex gap-4 p-3 rounded-lg cursor-pointer",
                "bg-card border border-border",
                "hover:shadow-sm hover:border-border transition-all duration-200"
              )}
            >
              <div className="flex-shrink-0 w-14 h-14 rounded-lg flex flex-col items-center justify-center text-center bg-muted">
                <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  {format(new Date(event.startTime), "MMM")}
                </span>
                <span className="text-xl font-bold leading-none text-foreground">
                  {format(new Date(event.startTime), "dd")}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground text-sm group-hover:text-foreground transition-colors truncate">
                  {event.title}
                </h3>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(event.startTime), "h:mm a")} - {format(new Date(event.endTime), "h:mm a")}
                </p>
                {event.location && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3" />
                    {event.location}
                  </p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
              <Calendar className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium text-sm">No upcoming events</p>
            <p className="text-xs text-muted-foreground mt-1">Check back later</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EventSkeleton() {
  return (
    <div className="flex gap-4 p-3 border border-border rounded-lg">
      <Skeleton className="w-14 h-14 rounded-lg" />
      <div className="flex-1">
        <Skeleton className="h-4 w-3/4 mb-2" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-1/2 mt-1" />
      </div>
    </div>
  );
}
